/**
 * Order Management — Master Data Hub
 * Navigation hub for all master data entities.
 *
 * client/src/components/pages/Settings/OrderManagementSettings.tsx (converted from .jsx, ST-00216)
 *
 * ST-00227 (audit ORD-20): a single-entity sync answers `{ [entity]: result }` (a bare result
 * from an older server is wrapped here), every result shows new / updated / unchanged / kept
 * as edited and the names that failed, and the last run (time and how it started) is the
 * server's, so it survives a restart and belongs to this organisation.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  MapPin,
  Building2,
  Truck,
  Users,
  Route,
  ArrowLeft,
  ArrowRight,
  Database,
  Loader,
  FileSpreadsheet,
  ArrowRightLeft,
  RefreshCw,
  Trash2,
  CheckCircle,
  AlertCircle,
  Clock,
  type LucideIcon,
} from 'lucide-react';
import orderService from '../../../services/orderService';
import { useAuth } from '../../../types/commonComponents';
import { formatAppDateTime } from '../../../utils/dateUtils';
import type {
  ApiErrorLike,
  MasterDataEntityKey,
  MasterDataEntitySyncResult,
  MasterDataSyncResult,
  MasterDataSyncStatus,
} from '../../../types/clientPages/orderManagement';

interface MasterDataEntity {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  path: string | null;
  color: string;
  bgColor: string;
  countKey: MasterDataEntityKey | null;
  comingSoon?: boolean;
}

interface SyncEntity {
  key: MasterDataEntityKey;
  label: string;
  icon: LucideIcon;
  color: string;
}

/** Which confirmation is open: Sync All or Clean Up. */
type ConfirmKind = 'all' | 'cleanup';

const ENTITIES: MasterDataEntity[] = [
  {
    id: 'products',
    label: 'Products',
    description: 'Product catalog and categories',
    icon: Package,
    path: '/order-management/products',
    color: '#0d9488',
    bgColor: 'rgba(13,148,136,0.08)',
    countKey: 'products',
  },
  {
    id: 'locations',
    label: 'Locations',
    description: 'Dispatch and receipt points',
    icon: MapPin,
    path: '/order-management/locations',
    color: '#6366f1',
    bgColor: 'rgba(99,102,241,0.08)',
    countKey: 'locations',
  },
  {
    id: 'companies',
    label: 'Companies',
    description: 'Haulers, suppliers, and customers',
    icon: Building2,
    path: '/order-management/companies',
    color: '#8b5cf6',
    bgColor: 'rgba(139,92,246,0.08)',
    countKey: 'companies',
  },
  {
    id: 'vehicles',
    label: 'Vehicles',
    description: 'Fleet registry and whitelisting',
    icon: Truck,
    path: '/order-management/vehicles',
    color: '#0ea5e9',
    bgColor: 'rgba(14,165,233,0.08)',
    countKey: 'vehicles',
  },
  {
    id: 'drivers',
    label: 'Drivers',
    description: 'Driver registry and whitelisting',
    icon: Users,
    path: '/order-management/drivers',
    color: '#f59e0b',
    bgColor: 'rgba(245,158,11,0.08)',
    countKey: 'drivers',
  },
  {
    id: 'bulk-upload',
    label: 'Bulk Upload',
    description: 'Import vehicles, drivers & allocations from CSV',
    icon: FileSpreadsheet,
    path: '/order-management/bulk-upload',
    color: '#7c3aed',
    bgColor: 'rgba(124,58,237,0.08)',
    countKey: null,
  },
  {
    id: 'allocate',
    label: 'Truck Allocation',
    description: 'Assign trucks to active orders',
    icon: ArrowRightLeft,
    path: '/order-management/allocate',
    color: '#059669',
    bgColor: 'rgba(5,150,105,0.08)',
    countKey: null,
  },
  {
    id: 'routes',
    label: 'Routes',
    description: 'Transport route definitions',
    icon: Route,
    path: null,
    color: 'var(--text-faint)',
    bgColor: 'var(--surface-sunken)',
    countKey: null,
    comingSoon: true,
  },
];

// Dialog copy (ORD-5). Keep it to what the server actually does: Clean Up only touches
// the caller's organisation, the original sample rows and sync-made duplicate
// locations; the sync never changes or brings back what a person edited or removed.
const CLEANUP_CONFIRM_TEXT =
  'This deactivates the original sample records (LOC001–LOC005, COMP001–COMP005, ' +
  'PROD001–PROD005 and three demo locations), duplicate locations the sync itself ' +
  'created, and driver links to vehicles or drivers that are already inactive, in your ' +
  'organisation only. Nothing else is changed.';
const SYNC_ALL_CONFIRM_TEXT =
  'This runs the clean-up above, then adds Locations, Companies, Products, Vehicles and ' +
  'Drivers that appear in LexiBridge ticket data but are not in the lists yet. Records you ' +
  'have edited or deactivated are left as they are. A driver or vehicle you renamed may be ' +
  'added again under the name on the tickets.';

const SYNC_ENTITIES: SyncEntity[] = [
  { key: 'locations', label: 'Locations', icon: MapPin, color: '#6366f1' },
  { key: 'companies', label: 'Companies', icon: Building2, color: '#8b5cf6' },
  { key: 'products', label: 'Products', icon: Package, color: '#0d9488' },
  { key: 'vehicles', label: 'Vehicles', icon: Truck, color: '#0ea5e9' },
  { key: 'drivers', label: 'Drivers', icon: Users, color: '#f59e0b' },
];

/**
 * A single-entity sync answers `{ [entity]: result, elapsed_ms }`; an older server answered
 * the bare entity result. Wrap the bare shape so the page reads one shape.
 */
function normaliseSyncResult(
  result: MasterDataSyncResult | MasterDataEntitySyncResult | null | undefined,
  entity: MasterDataEntityKey | null
): MasterDataSyncResult {
  if (!result) return {};
  if (entity && 'created' in result && typeof result.created === 'number') {
    const bare = result as MasterDataEntitySyncResult;
    return { [entity]: bare, elapsed_ms: bare.elapsed_ms };
  }
  return result as MasterDataSyncResult;
}

const MAX_ERRORS_SHOWN = 5;

/** One entity's figures: always every count, then the names that failed. */
const EntityResult = ({
  label,
  r,
  id,
}: {
  label: string;
  r: MasterDataEntitySyncResult;
  id: string;
}) => {
  const errors = r.errors || [];
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2" data-testid={`sync-result-${id}`}>
      <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
        {label}
      </div>
      <div
        className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs tabular-nums"
        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
      >
        <span className="text-emerald-600">{r.created} new</span>
        <span className="text-amber-600">{r.updated} updated</span>
        <span className="text-slate-400">{r.skipped} unchanged</span>
        {r.preserved != null && (
          <span className="text-slate-500">{r.preserved} kept as edited</span>
        )}
        {errors.length > 0 && <span className="text-red-500">{errors.length} failed</span>}
      </div>
      {errors.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-[11px] text-red-600">
          {errors.slice(0, MAX_ERRORS_SHOWN).map((e, i) => (
            <li key={`${e.name}-${i}`} className="break-words">
              <span className="font-semibold">{e.name}</span>: {e.error}
            </li>
          ))}
          {errors.length > MAX_ERRORS_SHOWN && (
            <li className="text-red-500">and {errors.length - MAX_ERRORS_SHOWN} more</li>
          )}
        </ul>
      )}
    </div>
  );
};

const TRIGGER_TEXT: Record<string, string> = {
  scheduled: 'nightly sync',
  manual: 'run from this page',
};

const OrderManagementSettings = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  // Sync and Clean Up are admin-only on the server (settings.manage); hide them otherwise.
  const canRunSync = hasPermission('settings.manage');
  const [counts, setCounts] = useState<Partial<Record<MasterDataEntityKey, number>>>({});
  const [loading, setLoading] = useState(true);

  // Sync state
  const [syncStatus, setSyncStatus] = useState<MasterDataSyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingEntity, setSyncingEntity] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<MasterDataSyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null | undefined>(null);
  const [showConfirm, setShowConfirm] = useState<ConfirmKind | null>(null); // 'all' | 'cleanup' | entity key

  const fetchCounts = useCallback(async () => {
    try {
      const [products, locations, companies, vehicles, drivers] = await Promise.allSettled([
        orderService.getProducts(),
        orderService.getLocations(),
        orderService.getCompanies(),
        orderService.getVehicles(),
        orderService.getDrivers(),
      ]);
      setCounts({
        products: products.status === 'fulfilled' ? (products.value.products?.length ?? 0) : 0,
        locations: locations.status === 'fulfilled' ? (locations.value.locations?.length ?? 0) : 0,
        companies: companies.status === 'fulfilled' ? (companies.value.companies?.length ?? 0) : 0,
        vehicles: vehicles.status === 'fulfilled' ? (vehicles.value.vehicles?.length ?? 0) : 0,
        drivers: drivers.status === 'fulfilled' ? (drivers.value.drivers?.length ?? 0) : 0,
      });
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const fetchSyncStatus = useCallback(async () => {
    try {
      setSyncStatus(await orderService.getMasterDataSyncStatus());
    } catch {
      /* the hub works without it */
    }
  }, []);

  useEffect(() => {
    fetchSyncStatus();
  }, [fetchSyncStatus]);

  const handleSync = async (entity: MasterDataEntityKey | null = null) => {
    setShowConfirm(null);
    setSyncing(true);
    setSyncingEntity(entity);
    setSyncResult(null);
    setSyncError(null);
    try {
      const data = await orderService.triggerMasterDataSync(entity);
      setSyncResult(normaliseSyncResult(data.result, entity));
      setSyncStatus((prev) => ({
        ...prev,
        lastSyncAt: new Date().toISOString(),
        lastSyncTrigger: 'manual',
      }));
      await Promise.all([fetchCounts(), fetchSyncStatus()]);
    } catch (err) {
      setSyncError((err as ApiErrorLike).response?.data?.error || (err as ApiErrorLike).message);
    }
    setSyncing(false);
    setSyncingEntity(null);
  };

  const handleCleanup = async () => {
    setShowConfirm(null);
    setSyncing(true);
    setSyncingEntity('cleanup');
    setSyncResult(null);
    setSyncError(null);
    try {
      const data = await orderService.triggerMasterDataCleanup();
      setSyncResult({ cleanup: data.result });
      await fetchCounts();
    } catch (err) {
      setSyncError((err as ApiErrorLike).response?.data?.error || (err as ApiErrorLike).message);
    }
    setSyncing(false);
    setSyncingEntity(null);
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Back */}
      <button
        onClick={() => navigate('/settings')}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Settings
      </button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-600 to-teal-700 flex items-center justify-center shadow-sm">
            <Database className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 leading-tight">Master Data</h1>
            <p className="text-sm text-slate-500">Core reference data for order management</p>
          </div>
        </div>
      </div>

      {/* Entity Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ENTITIES.map((entity, i) => {
          const Icon = entity.icon;
          const count = entity.countKey ? counts[entity.countKey] : null;
          return (
            <button
              key={entity.id}
              onClick={() => entity.path && navigate(entity.path)}
              disabled={entity.comingSoon}
              className="group relative bg-surface rounded-xl border border-slate-200 p-5 text-left transition-all duration-200 hover:border-slate-300 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:border-slate-200 animate-slideUp"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {/* Icon + Count row */}
              <div className="flex items-start justify-between mb-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-transform duration-200 group-hover:scale-105"
                  style={{ backgroundColor: entity.bgColor }}
                >
                  <Icon className="w-5 h-5" style={{ color: entity.color }} />
                </div>
                {loading ? (
                  <Loader className="w-4 h-4 text-slate-300 animate-spin" />
                ) : count !== null ? (
                  <span
                    className="text-lg font-bold text-slate-800 tabular-nums"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  >
                    {count}
                  </span>
                ) : entity.comingSoon ? (
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded">
                    Soon
                  </span>
                ) : null}
              </div>

              {/* Label + description */}
              <h3 className="text-sm font-bold text-slate-800 mb-0.5">{entity.label}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{entity.description}</p>

              {/* Arrow indicator */}
              {!entity.comingSoon && (
                <div className="absolute right-4 bottom-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* ─── Sync from Operations Data ─── */}
      <div className="mt-10 bg-surface rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <RefreshCw className="w-4.5 h-4.5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-slate-800">Sync from Operations Data</h2>
              <p className="text-xs text-slate-400 truncate">
                Populate master data from LexiBridge ticket data
                {syncStatus?.lastSyncAt && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 text-slate-400"
                    data-testid="last-sync"
                  >
                    <Clock className="w-3 h-3" />
                    Last: {formatAppDateTime(syncStatus.lastSyncAt)}
                    {syncStatus.lastSyncTrigger && TRIGGER_TEXT[syncStatus.lastSyncTrigger] && (
                      <> ({TRIGGER_TEXT[syncStatus.lastSyncTrigger]})</>
                    )}
                  </span>
                )}
              </p>
            </div>
          </div>
          {canRunSync ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowConfirm('cleanup')}
                disabled={syncing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-40"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Clean Up Test Data
              </button>
              <button
                onClick={() => setShowConfirm('all')}
                disabled={syncing}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-40"
              >
                {syncing && !syncingEntity ? (
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
                Sync All
              </button>
            </div>
          ) : (
            <p className="text-xs text-slate-400 flex-shrink-0">
              Only an administrator can run a sync or clean-up.
            </p>
          )}
        </div>

        {/* Per-entity sync row */}
        {canRunSync && (
          <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {SYNC_ENTITIES.map((ent) => (
              <button
                key={ent.key}
                onClick={() => handleSync(ent.key)}
                disabled={syncing}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-40"
              >
                {syncing && syncingEntity === ent.key ? (
                  <Loader className="w-3.5 h-3.5 animate-spin" style={{ color: ent.color }} />
                ) : (
                  <ent.icon className="w-3.5 h-3.5" style={{ color: ent.color }} />
                )}
                {ent.label}
              </button>
            ))}
          </div>
        )}

        {/* Sync progress / results */}
        {syncing && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-2 text-xs text-indigo-600">
            <Loader className="w-3.5 h-3.5 animate-spin" />
            Syncing {syncingEntity === 'cleanup' ? 'cleanup' : syncingEntity || 'all entities'}...
            This may take a minute for vehicles and drivers.
          </div>
        )}

        {syncError && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-2 text-xs text-red-600">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {syncError}
          </div>
        )}

        {syncResult && !syncing && (
          <div className="px-5 py-3 border-t border-slate-100">
            {(() => {
              const failed = SYNC_ENTITIES.some(
                (ent) => (syncResult[ent.key]?.errors?.length ?? 0) > 0
              );
              return (
                <div
                  className={`flex items-center gap-2 text-xs font-semibold mb-2 ${failed ? 'text-amber-600' : 'text-emerald-600'}`}
                >
                  {failed ? (
                    <AlertCircle className="w-3.5 h-3.5" />
                  ) : (
                    <CheckCircle className="w-3.5 h-3.5" />
                  )}
                  {failed ? 'Sync finished — some records could not be saved' : 'Sync complete'}
                  {syncResult.elapsed_ms != null && (
                    <span className="text-slate-400 font-normal">
                      ({(syncResult.elapsed_ms / 1000).toFixed(1)}s)
                    </span>
                  )}
                </div>
              );
            })()}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {SYNC_ENTITIES.map((ent) => {
                const r = syncResult[ent.key];
                return r ? (
                  <EntityResult key={ent.key} id={ent.key} label={ent.label} r={r} />
                ) : null;
              })}
              {syncResult.cleanup && (
                <div className="bg-slate-50 rounded-lg px-3 py-2 col-span-full">
                  <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                    Cleaned Up
                  </div>
                  <div
                    className="flex items-baseline gap-3 text-xs tabular-nums"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  >
                    {Object.entries(syncResult.cleanup).map(
                      ([k, v]) =>
                        v > 0 && (
                          <span key={k} className="text-slate-500">
                            {k}: {v}
                          </span>
                        )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setShowConfirm(null)}
        >
          <div
            className="bg-surface rounded-xl shadow-xl p-6 max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-bold text-slate-800 mb-2">
              {showConfirm === 'cleanup' ? 'Clean Up Test Data?' : 'Sync All Master Data?'}
            </h3>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              {showConfirm === 'cleanup' ? CLEANUP_CONFIRM_TEXT : SYNC_ALL_CONFIRM_TEXT}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirm(null)}
                className="px-4 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={showConfirm === 'cleanup' ? handleCleanup : () => handleSync()}
                className="px-4 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                {showConfirm === 'cleanup' ? 'Clean Up' : 'Sync All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagementSettings;
